import { useMemo, useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Printer,
  RotateCcw,
  ListChecks,
  ExternalLink,
  Info,
  CheckCircle2,
  AlertTriangle,
  ShieldAlert,
} from "lucide-react";

/* ══════════════════════════════════════════════════════════════════════
 * Independent Status Reality Check
 *
 * Front-end only. Nothing is sent to the server, nothing is stored
 * anywhere (no localStorage, no database, no names/emails collected).
 * All scoring happens in the browser from the supplied logic below.
 *
 * Destination links — easy to update in one place if final URLs change.
 * ══════════════════════════════════════════════════════════════════════ */
const LINKS = {
  cest: "https://www.gov.uk/guidance/check-employment-status-for-tax",
  understandingIndependentCare: "/workspace/carer-starter#guidance",
  ir35Article: "/wim-blog/ir35-explained-self-employed-carers",
};

/* ── Context questions (not scored) ─────────────────────────────────── */
const CONTEXT_QUESTIONS = [
  {
    id: "c1",
    question: "Who is completing the Reality Check?",
    options: [
      { letter: "A", text: "I am an independent carer" },
      { letter: "B", text: "I am a family member or person arranging care" },
      { letter: "C", text: "We are completing it together" },
      { letter: "D", text: "Other" },
    ],
  },
  {
    id: "c2",
    question: "How is the care work arranged?",
    options: [
      { letter: "A", text: "Directly between a family or individual and a sole-trader carer", trigger: false },
      { letter: "B", text: "Through the carer\u2019s own limited company", trigger: true },
      { letter: "C", text: "Through an agency or umbrella company", trigger: true },
      { letter: "D", text: "Through an introductory service", trigger: false },
      { letter: "E", text: "I am not sure", trigger: true },
    ],
  },
];

/* ── The 12 scored questions ─────────────────────────────────────────
 * Option letters map A/B/C → 0/1/2 points. Questions 1-5 are "core". */
const SCORED_QUESTIONS = [
  {
    id: "q1",
    n: 1,
    core: true,
    question: "Can the carer decline future work?",
    options: [
      { letter: "A", points: 0, text: "The carer can decline future shifts or bookings without being penalised. Once work has been accepted, they are expected to honour that commitment or follow the agreed arrangements for cover." },
      { letter: "B", points: 1, text: "The carer can sometimes decline work, but there is an expectation that they will usually accept what is offered." },
      { letter: "C", points: 2, text: "The carer is expected to accept the rota or shifts provided and may lose the arrangement or face consequences for refusing them." },
    ],
    noteTitle: "Why this matters",
    note: "An independent carer should normally have a genuine choice about whether to accept new work. This does not mean that an accepted booking can simply be abandoned.",
  },
  {
    id: "q2",
    n: 2,
    core: true,
    question: "Is there an ongoing obligation to offer and accept work?",
    options: [
      { letter: "A", points: 0, text: "Neither side is obliged to offer or accept future work. Individual shifts, blocks or periods of care are agreed as required." },
      { letter: "B", points: 1, text: "There is a regular arrangement, but future hours can genuinely be discussed, changed or declined by either side." },
      { letter: "C", points: 2, text: "The family is expected to continue providing set work and the carer is expected to remain available and accept it on an ongoing basis." },
    ],
    noteTitle: "Why this matters",
    note: "A regular arrangement can still be independent. The important question is whether it has become an ongoing obligation resembling a permanent job.",
  },
  {
    id: "q3",
    n: 3,
    core: true,
    question: "Could the carer provide a substitute in practice?",
    options: [
      { letter: "A", points: 0, text: "The carer has a genuine and practical right to arrange and pay a suitably qualified and appropriately checked replacement. The family may reject someone who cannot meet the agreed care, qualification or safeguarding requirements." },
      { letter: "B", points: 1, text: "The agreement mentions substitution, but it is unlikely to happen in practice, or the family or agency would actually arrange and pay for cover." },
      { letter: "C", points: 2, text: "The named carer must personally provide all the care and has no practical right to send another suitable carer in their place." },
    ],
    noteTitle: "Important care-sector note",
    note: "A family should never be expected to accept an unknown, unchecked or unsuitable person. This question is not suggesting that safeguarding or consent should be bypassed. It asks whether the carer is genuinely responsible for providing the service, including appropriate cover, or whether only that named individual can perform the work. A requirement for personal service is one of several factors HMRC considers. A substitution clause that could not realistically be used carries less weight than one that works in practice.",
  },
  {
    id: "q4",
    n: 4,
    core: true,
    question: "Who decides how the agreed care is delivered?",
    options: [
      { letter: "A", points: 0, text: "The person receiving care or their representative identifies the required outcomes, preferences, care plan and safety requirements. Within those boundaries, the carer uses their skills and professional judgement to organise the work." },
      { letter: "B", points: 1, text: "Decisions are shared, but the family gives regular instructions about how particular tasks should be carried out." },
      { letter: "C", points: 2, text: "The family closely directs and supervises how, when and in what order the carer must perform the work, beyond reasonable care instructions, preferences and safety requirements." },
    ],
    noteTitle: "Why this matters",
    note: "Following an agreed care plan, medication instructions, risk assessment, safeguarding requirements or the wishes of the person receiving care does not by itself mean the carer is employed. The concern is broader managerial control over how the carer performs their role. HMRC considers control over what, how, when and where work is carried out.",
  },
  {
    id: "q5",
    n: 5,
    core: true,
    question: "Who controls the hours, rota and availability?",
    options: [
      { letter: "A", points: 0, text: "The carer offers their availability and the parties mutually agree individual bookings or defined periods of work." },
      { letter: "B", points: 1, text: "There is a regular rota that has been jointly agreed, and changes require reasonable coordination to protect continuity of care." },
      { letter: "C", points: 2, text: "The family sets the carer\u2019s hours and rota, and the carer must obtain permission to change availability, take time away or decline future dates." },
    ],
    noteTitle: "Why this matters",
    note: "Care often has to happen at particular times, so fixed visit times or an agreed live-in rotation are not automatically signs of employment. The issue is whether the timing is genuinely negotiated or imposed and managed as it would be for a member of staff.",
  },
  {
    id: "q6",
    n: 6,
    core: false,
    question: "Can the carer work for other clients?",
    options: [
      { letter: "A", points: 0, text: "Yes. The carer works for, or actively offers similar care services to, other clients." },
      { letter: "B", points: 1, text: "Yes. The carer is free to work for others but currently has only one client because of availability or personal circumstances." },
      { letter: "C", points: 2, text: "No. The carer is restricted from working for others, needs permission, or the family expects first call on their availability outside agreed bookings." },
    ],
    noteTitle: "Why this matters",
    note: "Providing similar services to different clients can indicate that the carer is operating a business. However, having only one current client does not automatically make someone employed.",
  },
  {
    id: "q7",
    n: 7,
    core: false,
    question: "Who decides the rate and contractual terms?",
    options: [
      { letter: "A", points: 0, text: "The carer sets or negotiates their own rates and terms and can propose changes for future work." },
      { letter: "B", points: 1, text: "The family initially proposes the rate and terms, but genuine negotiation is possible." },
      { letter: "C", points: 2, text: "The rate and terms are imposed by the family or another organisation, and the carer has little or no ability to negotiate them." },
    ],
    noteTitle: "Why this matters",
    note: "Being able to negotiate supports independence, but payment method or an hourly rate does not decide status by itself. Employees and self-employed people can both be paid hourly, daily or by the job.",
  },
  {
    id: "q8",
    n: 8,
    core: false,
    question: "How is the carer paid?",
    options: [
      { letter: "A", points: 0, text: "The carer submits invoices, manages their own tax and National Insurance, and receives no paid holiday, sick pay or employee benefits." },
      { letter: "B", points: 1, text: "The carer receives regular payments, but invoicing, tax responsibilities or the basis of payment are unclear." },
      { letter: "C", points: 2, text: "The carer receives payslips, PAYE deductions, paid holiday, sick pay or other benefits normally associated with employment." },
    ],
    noteTitle: "Why this matters",
    note: "Invoicing is commonly associated with self-employment, but producing an invoice does not override the reality of the working relationship. Acas identifies invoices, control, multiple clients and genuine substitution as common self-employment features.",
  },
  {
    id: "q9",
    n: 9,
    core: false,
    question: "Does the carer carry meaningful business risk?",
    options: [
      { letter: "A", points: 0, text: "The carer bears meaningful unreimbursed costs required to provide the service and could make a profit or suffer a financial loss from the arrangement." },
      { letter: "B", points: 1, text: "The carer pays some ordinary business expenses but has limited financial risk within this particular arrangement." },
      { letter: "C", points: 2, text: "Payment is effectively guaranteed, all required costs are reimbursed and the carer has no realistic possibility of making a financial loss." },
    ],
    noteTitle: "Care-sector note",
    note: "This does not mean the carer should supply specialist equipment belonging to the person receiving care, such as hoists, profiling beds or other client-specific equipment. The absence of significant financial risk does not automatically mean employment. HMRC treats financial risk as one factor among several.",
  },
  {
    id: "q10",
    n: 10,
    core: false,
    question: "How is the carer presented and managed?",
    options: [
      { letter: "A", points: 0, text: "The carer is clearly presented as an independent service provider and manages their own business administration, records and professional responsibilities." },
      { letter: "B", points: 1, text: "The relationship is informal, and it is not always clear whether the carer is acting as an independent business or as part of the family\u2019s care team." },
      { letter: "C", points: 2, text: "The carer is treated as a member of staff, managed through employee-style supervision or disciplinary procedures, or presented to others as working for the family or organisation." },
    ],
    noteTitle: "Why this matters",
    note: "How someone is integrated into and presented by the hirer can contribute to the overall picture. HMRC considers whether a person appears to work for the hirer or on behalf of their own business.",
  },
  {
    id: "q11",
    n: 11,
    core: false,
    special: "contract-mismatch",
    question: "Does the written agreement match what happens in practice?",
    options: [
      { letter: "A", points: 0, text: "Yes. The agreement accurately reflects how the arrangement operates day to day." },
      { letter: "B", points: 1, text: "There is no written agreement, but both sides have a reasonably clear and shared understanding of how the arrangement works." },
      { letter: "C", points: 2, text: "The agreement describes the carer as self-employed or includes independent-working clauses, but those clauses do not reflect what actually happens." },
    ],
    noteTitle: "Why this matters",
    note: "A written label is not conclusive. Government guidance says employment status is governed mainly by the reality of the relationship, and contractual wording cannot decide status where it conflicts with actual practice.",
  },
  {
    id: "q12",
    n: 12,
    core: false,
    question: "Does the carer operate a recognisable business?",
    options: [
      { letter: "A", points: 0, text: "The carer actively runs their own care business\u2014for example, marketing their services, maintaining business records and insurance, using service agreements and seeking or serving different clients." },
      { letter: "B", points: 1, text: "The carer has taken some business steps but remains substantially dependent on this one arrangement." },
      { letter: "C", points: 2, text: "There is little separate business activity, and the arrangement operates mainly like a continuing job with one family or organisation." },
    ],
    noteTitle: "Why this matters",
    note: "No single business feature proves self-employment. The purpose is to consider whether the carer appears to be in business on their own account or working as part of someone else\u2019s organisation.",
  },
];

const SCORED_IDS = SCORED_QUESTIONS.map((q) => q.id);
const Q11_INDEX = SCORED_QUESTIONS.findIndex((q) => q.id === "q11");

/* ── Result content ──────────────────────────────────────────────────── */
const RESULT_CONTENT = {
  green: {
    key: "green",
    outcomeLabel: "Strong signs of independent working",
    heading: "Your answers show several signs of independent working",
    intro: "The arrangement appears to contain a number of features commonly associated with a carer operating their own business.",
    bulletsLabel: "These may include:",
    bullets: [
      "genuine choice over accepting future work;",
      "negotiated bookings and terms;",
      "freedom to work for other clients;",
      "appropriate control over how the service is delivered; and",
      "business responsibility for invoices, records and operating costs.",
    ],
    calloutTitle: "This is not a legal determination",
    calloutParas: [
      "Employment status depends on the complete arrangement. A green result does not guarantee that HMRC, a court or an employment tribunal would reach the same conclusion.",
      "Review any amber or red answers shown below and check that the working practice continues to match the written agreement.",
    ],
    tone: "green",
  },
  amber: {
    key: "amber",
    outcomeLabel: "Mixed picture \u2014 review recommended",
    heading: "Some parts of the arrangement may need attention",
    intro: "Your answers show a mixture of independent and employment-like features. This does not automatically mean that the carer is employed. However, the arrangement may be unclear or may have changed over time.",
    nextStepsLabel: "Suggested next steps",
    nextSteps: [
      "Discuss the flagged areas together.",
      "Check whether the written agreement matches what happens in practice.",
      "Do not rely on changing contractual wording alone\u2014the actual working arrangement must also reflect it.",
      "Complete HMRC\u2019s official CEST tool for tax status.",
      "Seek advice from HMRC, Acas, an accountant or an employment-status professional where necessary.",
    ],
    tone: "amber",
  },
  red: {
    key: "red",
    outcomeLabel: "Several employment-like features",
    heading: "This arrangement contains significant employment-status warning signs",
    intro: "Several answers suggest that the reality of the arrangement may look more like employment or worker status than a business-to-client relationship.",
    bulletsLabel: "Warning signs may include:",
    bullets: [
      "little genuine ability to refuse work;",
      "an ongoing obligation to remain available;",
      "extensive control over hours and working methods;",
      "restrictions on working for other clients;",
      "employee-style holiday or absence arrangements; or",
      "contractual terms that do not match what happens in practice.",
    ],
    calloutTitle: "Do not rely solely on the \u201cself-employed\u201d label",
    calloutParas: [
      "Registering as self-employed, issuing invoices or signing a contract for services does not settle employment status if the reality of the arrangement points in another direction.",
      "This result does not declare that the carer is employed. It means the arrangement should be checked more formally before either side continues to rely upon self-employed status.",
    ],
    nextStepsLabel: "Recommended next steps",
    nextSteps: [
      "Review every red answer together.",
      "Avoid simply rewriting the contract without changing the underlying practice.",
      "Complete HMRC\u2019s official CEST tool.",
      "Obtain advice about both tax status and employment rights.",
      "Review whether the arrangement should be restructured or operated under a different employment model.",
    ],
    tone: "red",
  },
};

const NOTICE_TEXT =
  "You can continue with the Reality Check, but additional tax rules may apply to agency workers, intermediaries and people working through limited companies. Your result should not be relied upon to decide tax status. HMRC\u2019s Check Employment Status for Tax tool should also be completed. HMRC\u2019s CEST tool may be used by workers, hirers, agencies and third parties. It gives HMRC\u2019s view of tax status based on the information provided.";

const SPECIAL_RESULT = {
  title: "Special result: Agency, umbrella or limited-company arrangement",
  subheading: "Additional status rules may apply",
  body: "You indicated that the care is provided through an agency, umbrella company, intermediary or the carer\u2019s own limited company. The questions in this Reality Check may still help you identify areas to examine, but the score should not be used on its own. Agency tax rules or the off-payroll working rules may apply, depending on how the arrangement is structured.",
};

/* ── Scoring ──────────────────────────────────────────────────────────── */
function computeBand(scoredAnswers) {
  const total = SCORED_QUESTIONS.reduce((sum, q, i) => {
    const idx = scoredAnswers[q.id];
    return sum + (idx == null ? 0 : q.options[idx].points);
  }, 0);
  let band = total <= 5 ? "green" : total <= 12 ? "amber" : "red";

  const coreTwos = SCORED_QUESTIONS.filter(
    (q) => q.core && scoredAnswers[q.id] != null && q.options[scoredAnswers[q.id]].points === 2
  ).length;
  const q11Two =
    scoredAnswers.q11 != null && SCORED_QUESTIONS[Q11_INDEX].options[scoredAnswers.q11].points === 2;

  if (coreTwos >= 3) {
    band = "red";
  } else if ((coreTwos === 2 || q11Two) && band === "green") {
    band = "amber";
  }
  return band;
}

function getFlaggedQuestions(scoredAnswers) {
  return SCORED_QUESTIONS.filter((q) => {
    const idx = scoredAnswers[q.id];
    return idx != null && q.options[idx].points >= 1;
  }).map((q) => ({
    n: q.n,
    question: q.question,
    answerText: q.options[scoredAnswers[q.id]].text,
  }));
}

/* ── Small UI helpers ────────────────────────────────────────────────── */
function ToolHeader({ onReview, onRestart, showControls }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-6 print:hidden">
      <Link
        to="/workspace/carer-starter"
        className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink"
        data-testid="irc-back-to-hub"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Carer Starter Hub
      </Link>
      {showControls && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onReview}
            className="btn-outline text-xs !py-2 !px-3"
            data-testid="irc-review-answers-header"
          >
            <ListChecks className="w-3.5 h-3.5" strokeWidth={1.75} /> Review answers
          </button>
          <button
            type="button"
            onClick={onRestart}
            className="btn-outline text-xs !py-2 !px-3 !border-rose-200 !text-rose-700 hover:!bg-rose-50"
            data-testid="irc-restart-header"
          >
            <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.75} /> Start again
          </button>
        </div>
      )}
    </div>
  );
}

function ProgressBar({ label, current, total }) {
  const pct = total ? Math.round((current / total) * 100) : 0;
  return (
    <div className="mb-6" data-testid="irc-progress">
      <div className="flex items-center justify-between text-xs font-medium text-ink-muted mb-1.5">
        <span data-testid="irc-progress-label">{label}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full bg-teal transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function OptionList({ name, options, selected, onSelect }) {
  return (
    <fieldset className="space-y-3" data-testid={`irc-options-${name}`}>
      <legend className="sr-only">Choose the answer that most closely reflects what happens in practice</legend>
      {options.map((opt, i) => (
        <label
          key={opt.letter}
          className={`flex items-start gap-3 rounded-2xl border p-4 cursor-pointer transition-colors ${
            selected === i
              ? "border-teal bg-teal/5 ring-1 ring-teal/30"
              : "border-slate-200 bg-white hover:border-teal/40"
          }`}
          data-testid={`irc-option-${name}-${opt.letter}`}
        >
          <input
            type="radio"
            name={name}
            checked={selected === i}
            onChange={() => onSelect(i)}
            className="mt-1 h-4 w-4 accent-teal shrink-0"
          />
          <span className="text-sm md:text-base text-ink leading-relaxed">{opt.text}</span>
        </label>
      ))}
    </fieldset>
  );
}

function NavButtons({ onBack, onNext, backLabel = "Back", nextLabel = "Next", nextDisabled, showBack = true }) {
  return (
    <div className="mt-8 flex items-center justify-between print:hidden">
      {showBack ? (
        <button type="button" onClick={onBack} className="btn-outline text-sm" data-testid="irc-back-btn">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.75} /> {backLabel}
        </button>
      ) : (
        <span />
      )}
      <button
        type="button"
        onClick={onNext}
        disabled={nextDisabled}
        aria-disabled={nextDisabled}
        className={`btn-teal text-sm ${nextDisabled ? "opacity-40 cursor-not-allowed" : ""}`}
        data-testid="irc-next-btn"
      >
        {nextLabel} <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}

const TONE_STYLES = {
  green: { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-900", pill: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  amber: { border: "border-amber-200", bg: "bg-amber-50", text: "text-amber-900", pill: "bg-amber-100 text-amber-900", icon: AlertTriangle },
  red: { border: "border-rose-200", bg: "bg-rose-50", text: "text-rose-900", pill: "bg-rose-100 text-rose-900", icon: ShieldAlert },
};

function ResultButtons({ onReview, onPrint, onRestart }) {
  return (
    <div className="mt-6 flex flex-wrap gap-2 print:hidden">
      <button type="button" onClick={onReview} className="btn-outline text-sm" data-testid="irc-result-review">
        <ListChecks className="w-4 h-4" strokeWidth={1.75} /> Review my answers
      </button>
      <a
        href={LINKS.cest}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-outline text-sm"
        data-testid="irc-result-cest"
      >
        Use HMRC&rsquo;s official CEST tool <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.75} />
      </a>
      <Link to={LINKS.understandingIndependentCare} className="btn-outline text-sm" data-testid="irc-result-guidance">
        Read Understanding Independent Care
      </Link>
      <button type="button" onClick={onPrint} className="btn-outline text-sm" data-testid="irc-result-print">
        <Printer className="w-4 h-4" strokeWidth={1.75} /> Print or save this result
      </button>
      <button
        type="button"
        onClick={onRestart}
        className="btn-outline text-sm !border-rose-200 !text-rose-700 hover:!bg-rose-50"
        data-testid="irc-result-restart"
      >
        <RotateCcw className="w-4 h-4" strokeWidth={1.75} /> Start again
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
export default function IndependentStatusRealityCheck() {
  const [screen, setScreen] = useState("intro");
  const [answers, setAnswers] = useState({});
  const [preReviewScreen, setPreReviewScreen] = useState("intro");
  const headingRef = useRef(null);

  const showNotice = useMemo(() => {
    const idx = answers.c2;
    if (idx == null) return false;
    return !!CONTEXT_QUESTIONS[1].options[idx].trigger;
  }, [answers.c2]);

  const flow = useMemo(
    () => ["intro", "c1", "c2", ...(showNotice ? ["notice"] : []), ...SCORED_IDS, "result"],
    [showNotice]
  );

  useEffect(() => {
    if (headingRef.current) headingRef.current.focus();
  }, [screen]);

  const setAnswer = (id, idx) => setAnswers((prev) => ({ ...prev, [id]: idx }));

  const goTo = (id) => setScreen(id);
  const goNext = () => {
    const idx = flow.indexOf(screen);
    if (idx >= 0 && idx < flow.length - 1) setScreen(flow[idx + 1]);
  };
  const goBack = () => {
    const idx = flow.indexOf(screen);
    if (idx > 0) setScreen(flow[idx - 1]);
  };
  const restart = () => {
    setAnswers({});
    setScreen("intro");
  };
  const enterReview = () => {
    setPreReviewScreen(screen);
    setScreen("review");
  };
  const exitReview = () => setScreen(preReviewScreen);
  const onPrint = () => window.print();

  const isAgencyArrangement = showNotice;
  const scoredAnswers = answers;
  const allScoredAnswered = SCORED_IDS.every((id) => answers[id] != null);
  const band = allScoredAnswered ? computeBand(scoredAnswers) : null;
  const flagged = allScoredAnswered ? getFlaggedQuestions(scoredAnswers) : [];

  const contextQ = CONTEXT_QUESTIONS.find((q) => q.id === screen);
  const scoredQ = SCORED_QUESTIONS.find((q) => q.id === screen);

  return (
    <div className="min-h-screen bg-surface-carer" data-testid="page-independent-status-reality-check">
      <div className="container-jcc py-8 md:py-14 max-w-3xl">
        <ToolHeader onReview={enterReview} onRestart={restart} showControls={screen !== "intro" && screen !== "review"} />

        {/* ── Intro ─────────────────────────────────────────────────── */}
        {screen === "intro" && (
          <section data-testid="irc-screen-intro">
            <div className="overline text-teal mb-2">Independent Status Reality Check</div>
            <h1
              ref={headingRef}
              tabIndex={-1}
              className="font-display font-bold text-3xl md:text-4xl outline-none"
            >
              Does this care arrangement genuinely support independent working?
            </h1>
            <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 md:p-8 space-y-4 text-ink-muted leading-relaxed">
              <p>Calling a care arrangement &ldquo;self-employed&rdquo; does not automatically make it so.</p>
              <p>
                This free Reality Check asks about what actually happens in the working relationship
                {" \u2014 "}not merely what is written in the agreement. It will help you identify:
              </p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>signs that support independent working;</li>
                <li>areas where the arrangement is unclear; and</li>
                <li>features that may look more like employment or worker status.</li>
              </ul>
              <p>It takes around 3&ndash;5 minutes.</p>
              <p className="text-sm font-medium text-teal">Free to use &middot; Answers are not stored</p>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm">
                <p className="font-medium text-ink mb-1.5">Before you begin</p>
                <p>Please answer about one specific care arrangement.</p>
                <p className="mt-1.5">
                  Choose the answer that most closely reflects what happens in practice. If the written
                  agreement says one thing but the real arrangement works differently, answer according to
                  the reality.
                </p>
                <p className="mt-1.5">
                  This tool provides general educational information. It does not determine employment
                  status or replace HMRC, Acas, legal or professional advice.
                </p>
              </div>
            </div>
            <div className="mt-6">
              <button type="button" onClick={() => goTo("c1")} className="btn-teal" data-testid="irc-start-btn">
                Start the Reality Check <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </div>
          </section>
        )}

        {/* ── Context questions ────────────────────────────────────── */}
        {contextQ && (
          <section data-testid={`irc-screen-${contextQ.id}`}>
            <ProgressBar
              label={`About this arrangement \u00b7 Step ${contextQ.id === "c1" ? 1 : 2} of 2`}
              current={contextQ.id === "c1" ? 1 : 2}
              total={2}
            />
            <h1 ref={headingRef} tabIndex={-1} className="font-display font-semibold text-xl md:text-2xl outline-none">
              {contextQ.question}
            </h1>
            <div className="mt-5">
              <OptionList
                name={contextQ.id}
                options={contextQ.options}
                selected={answers[contextQ.id]}
                onSelect={(i) => setAnswer(contextQ.id, i)}
              />
            </div>
            <NavButtons
              onBack={goBack}
              onNext={goNext}
              nextDisabled={answers[contextQ.id] == null}
            />
          </section>
        )}

        {/* ── Conditional notice ───────────────────────────────────── */}
        {screen === "notice" && (
          <section data-testid="irc-screen-notice">
            <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6 md:p-8">
              <div className="flex items-center gap-2 text-amber-900 font-semibold mb-2">
                <Info className="w-5 h-5" strokeWidth={1.75} />
                <h1 ref={headingRef} tabIndex={-1} className="font-display text-lg md:text-xl outline-none">
                  Additional tax rules may apply
                </h1>
              </div>
              <p className="text-amber-900 leading-relaxed">{NOTICE_TEXT}</p>
            </div>
            <NavButtons onBack={goBack} onNext={goNext} nextLabel="Continue" />
          </section>
        )}

        {/* ── Scored questions ─────────────────────────────────────── */}
        {scoredQ && (
          <section data-testid={`irc-screen-${scoredQ.id}`}>
            <ProgressBar label={`Question ${scoredQ.n} of 12`} current={scoredQ.n} total={12} />
            <h1 ref={headingRef} tabIndex={-1} className="font-display font-semibold text-xl md:text-2xl outline-none">
              {scoredQ.question}
            </h1>
            <div className="mt-5">
              <OptionList
                name={scoredQ.id}
                options={scoredQ.options}
                selected={answers[scoredQ.id]}
                onSelect={(i) => setAnswer(scoredQ.id, i)}
              />
            </div>
            <div className="mt-4 rounded-2xl bg-teal/5 border border-teal/15 p-4 text-sm text-ink-muted leading-relaxed" data-testid={`irc-note-${scoredQ.id}`}>
              <div className="flex items-center gap-2 text-teal font-semibold mb-1">
                <Info className="w-4 h-4" strokeWidth={1.75} /> {scoredQ.noteTitle}
              </div>
              {scoredQ.note}
            </div>
            <NavButtons
              onBack={goBack}
              onNext={goNext}
              nextLabel={scoredQ.n === 12 ? "See my result" : "Next"}
              nextDisabled={answers[scoredQ.id] == null}
            />
          </section>
        )}

        {/* ── Result ───────────────────────────────────────────────── */}
        {screen === "result" && band && (
          <ResultScreen
            band={band}
            flagged={flagged}
            isAgencyArrangement={isAgencyArrangement}
            headingRef={headingRef}
            onReview={enterReview}
            onPrint={onPrint}
            onRestart={restart}
          />
        )}

        {/* ── Review answers ───────────────────────────────────────── */}
        {screen === "review" && (
          <section data-testid="irc-screen-review">
            <h1 ref={headingRef} tabIndex={-1} className="font-display font-semibold text-2xl outline-none">
              Your answers
            </h1>
            <p className="mt-2 text-sm text-ink-muted">A read-only summary of what you selected. Nothing here is stored.</p>
            <ol className="mt-6 space-y-4">
              {[...CONTEXT_QUESTIONS, ...SCORED_QUESTIONS].map((q, i) => {
                const idx = answers[q.id];
                return (
                  <li key={q.id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`irc-review-item-${q.id}`}>
                    <div className="text-xs font-medium text-ink-muted">Question {i + 1}</div>
                    <div className="font-display font-semibold text-ink mt-0.5">{q.question}</div>
                    <div className="mt-2 text-sm text-ink-muted leading-relaxed">
                      {idx == null ? (
                        <span className="italic">Not yet answered</span>
                      ) : (
                        q.options[idx].text
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
            <div className="mt-8 print:hidden">
              <button type="button" onClick={exitReview} className="btn-teal text-sm" data-testid="irc-review-back">
                <ArrowLeft className="w-4 h-4" strokeWidth={1.75} /> Back
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

/* ── Result screen ────────────────────────────────────────────────────── */
function ResultScreen({ band, flagged, isAgencyArrangement, headingRef, onReview, onPrint, onRestart }) {
  const r = RESULT_CONTENT[band];
  const tone = TONE_STYLES[r.tone];
  const Icon = tone.icon;

  return (
    <section data-testid="irc-screen-result">
      <span className={`pill ${tone.pill}`} data-testid="irc-result-outcome-pill">
        <Icon className="w-3.5 h-3.5 mr-1" /> {r.outcomeLabel}
      </span>
      <h1 ref={headingRef} tabIndex={-1} className="mt-3 font-display font-bold text-2xl md:text-3xl outline-none">
        {r.heading}
      </h1>
      <p className="mt-3 text-ink-muted leading-relaxed">{r.intro}</p>

      {r.bullets && (
        <div className="mt-4">
          <p className="text-sm font-medium text-ink">{r.bulletsLabel}</p>
          <ul className="list-disc pl-5 mt-2 text-ink-muted space-y-1 leading-relaxed">
            {r.bullets.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </div>
      )}

      {r.calloutTitle && (
        <div className={`mt-6 rounded-2xl border ${tone.border} ${tone.bg} p-5`} data-testid="irc-result-callout">
          <p className={`font-display font-semibold ${tone.text}`}>{r.calloutTitle}</p>
          {r.calloutParas.map((p) => (
            <p key={p} className={`mt-2 text-sm leading-relaxed ${tone.text}`}>
              {p}
            </p>
          ))}
        </div>
      )}

      {flagged.length > 0 && (
        <div className="mt-6" data-testid="irc-areas-to-review">
          <h2 className="font-display font-semibold text-lg">Areas to review</h2>
          <div className="mt-3 space-y-3">
            {flagged.map((f) => (
              <div key={f.n} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`irc-flagged-q${f.n}`}>
                <p className="text-sm font-medium text-ink">{f.question}</p>
                <p className="mt-1.5 text-sm text-ink-muted leading-relaxed">{f.answerText}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {r.nextSteps && (
        <div className="mt-6">
          <h2 className="font-display font-semibold text-lg">{r.nextStepsLabel}</h2>
          <ol className="mt-2 space-y-1.5 list-decimal pl-5 text-ink-muted leading-relaxed">
            {r.nextSteps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
        </div>
      )}

      <ResultButtons onReview={onReview} onPrint={onPrint} onRestart={onRestart} />

      {isAgencyArrangement && (
        <div className="mt-8 rounded-3xl border border-purple/20 bg-purple/5 p-6 md:p-8" data-testid="irc-special-agency-result">
          <span className="pill bg-purple/10 text-purple">{SPECIAL_RESULT.subheading}</span>
          <h2 className="mt-3 font-display font-semibold text-xl">{SPECIAL_RESULT.title}</h2>
          <p className="mt-2 text-ink-muted leading-relaxed">{SPECIAL_RESULT.body}</p>
          <div className="mt-5 flex flex-wrap gap-2 print:hidden">
            <a href={LINKS.cest} target="_blank" rel="noopener noreferrer" className="btn-outline text-sm" data-testid="irc-special-cest">
              Use HMRC&rsquo;s official CEST tool <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.75} />
            </a>
            <Link to={LINKS.ir35Article} className="btn-outline text-sm" data-testid="irc-special-ir35">
              Read JCC&rsquo;s IR35 guidance
            </Link>
            <button type="button" onClick={onReview} className="btn-outline text-sm" data-testid="irc-special-review">
              Review my Reality Check answers
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
